import React, { useState, useEffect, useRef } from 'react'
import { createResearchGroup, getAllResearchGroups, joinResearchGroup, getUserResearchGroups, updateResearchGroup, deleteResearchGroup } from '../services/firestore'
import { Search, Plus, Shield, Users, Lock, Unlock, Atom, Dna, Microscope, AlertCircle, CheckCircle2, Edit, Upload, X, MapPin, Mail, Phone, ExternalLink, Trash2, Image as ImageIcon } from 'lucide-react'
import { storage } from '../firebase/config'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { capitalizeName } from '../utils/formatters'
import './ResearchGroupsDashboard.css'

const ResearchGroupsDashboard = ({ user }) => {
  const [activeTab, setActiveTab] = useState('my_groups') // my_groups, explore, create
  
  // States for Explore
  const [allGroups, setAllGroups] = useState([])
  const [myGroups, setMyGroups] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  
  // States for Join Modal
  const [showJoinModal, setShowJoinModal] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [joinKeyword, setJoinKeyword] = useState('')
  const [joinError, setJoinError] = useState('')
  const [joinSuccess, setJoinSuccess] = useState('')
  const [isJoining, setIsJoining] = useState(false)

  // States for Create
  const [formData, setFormData] = useState({
    name: '',
    abbr: '',
    description: '',
    keyword: '',
    tags: ''
  })
  const [createError, setCreateError] = useState('')
  const [createSuccess, setCreateSuccess] = useState('')
  const [isCreating, setIsCreating] = useState(false)

  // States for Edit
  const [editingGroup, setEditingGroup] = useState(null)
  const [editFormData, setEditFormData] = useState({
    detailedDescription: '',
    projects: '',
    publications: '',
    contactEmail: ''
  })
  const [editImageFile, setEditImageFile] = useState(null)
  const [editImagePreview, setEditImagePreview] = useState('')
  const [editLogoFile, setEditLogoFile] = useState(null)
  const [editLogoPreview, setEditLogoPreview] = useState('')
  const [isUpdating, setIsUpdating] = useState(false)
  const [editError, setEditError] = useState('')
  const [editSuccess, setEditSuccess] = useState('')
  const fileInputRef = useRef(null)
  const logoInputRef = useRef(null)

  useEffect(() => {
    loadData()
  }, [user])

  const loadData = async () => {
    if (!user) return
    setLoading(true)
    try {
      const [allRes, myRes] = await Promise.all([
        getAllResearchGroups(),
        getUserResearchGroups(user.uid)
      ])
      
      if (allRes.success) setAllGroups(allRes.data)
      if (myRes.success) setMyGroups(myRes.data)
    } catch (e) {
      console.error(e)
    }
    setLoading(false)
  }

  // --- JOIN LOGIC ---
  const handleOpenJoin = (group) => {
    setSelectedGroup(group)
    setJoinKeyword('')
    setJoinError('')
    setJoinSuccess('')
    setShowJoinModal(true)
  }

  const handleJoinSubmit = async (e) => {
    e.preventDefault()
    if (!joinKeyword.trim()) {
      setJoinError('Por favor ingresa la palabra clave')
      return
    }

    setIsJoining(true)
    setJoinError('')
    
    try {
      const result = await joinResearchGroup(selectedGroup.id, joinKeyword, user.uid)
      if (result.success) {
        setJoinSuccess('¡Te has unido exitosamente al grupo!')
        setTimeout(() => {
          setShowJoinModal(false)
          loadData() // Reload
          setActiveTab('my_groups')
        }, 1500)
      } else {
        setJoinError(result.error)
      }
    } catch {
      setJoinError('Error al intentar unirse')
    }
    setIsJoining(false)
  }

  // --- CREATE LOGIC ---
  const handleCreateChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleCreateSubmit = async (e) => {
    e.preventDefault()
    if (!formData.name || !formData.abbr || !formData.description || !formData.keyword) {
      setCreateError('Todos los campos obligatorios deben estar llenos')
      return
    }

    setIsCreating(true)
    setCreateError('')
    setCreateSuccess('')

    try {
      const tagsArray = formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : []
      const newGroupData = {
        name: formData.name,
        abbr: formData.abbr,
        description: formData.description,
        keyword: formData.keyword,
        tags: tagsArray
      }

      const result = await createResearchGroup(newGroupData, user.uid)
      if (result.success) {
        setCreateSuccess('Grupo creado exitosamente')
        setFormData({ name: '', abbr: '', description: '', keyword: '', tags: '' })
        setTimeout(() => {
          loadData()
          setActiveTab('my_groups')
          setCreateSuccess('')
        }, 1500)
      } else {
        setCreateError(result.error)
      }
    } catch (err) {
      setCreateError('Error al crear el grupo: ' + err.message)
    }
    setIsCreating(false)
  }

  // --- EDIT LOGIC ---
  const handleOpenEdit = (group) => {
    setEditingGroup(group)
    setEditFormData({
      detailedDescription: group.detailedDescription || '',
      projects: group.projects ? group.projects.join('\n') : '',
      publications: group.publications ? group.publications.join('\n') : '',
      contactEmail: group.contactEmail || '',
      phone: group.phone || '',
      requirements: group.requirements ? group.requirements.join('\n') : ''
    })
    setEditImagePreview(group.image || '')
    setEditImageFile(null)
    setEditLogoPreview(group.logo || '')
    setEditLogoFile(null)
    setEditError('')
    setEditSuccess('')
    setActiveTab('edit_group')
  }

  const handleEditChange = (e) => {
    const { name, value } = e.target
    setEditFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleImageDrop = (e) => {
    e.preventDefault()
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleImageSelection(e.dataTransfer.files[0])
    }
  }

  const handleImageSelection = (file) => {
    if (!file.type.startsWith('image/')) {
      setEditError('Por favor selecciona un archivo de imagen válido.')
      return
    }
    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      setEditError('La imagen no debe superar los 5MB.')
      return
    }
    setEditError('')
    setEditImageFile(file)
    setEditImagePreview(URL.createObjectURL(file))
  }

  const handleLogoSelection = (file) => {
    if (!file.type.startsWith('image/')) {
      setEditError('Por favor selecciona un archivo de imagen válido para el logo.')
      return
    }
    if (file.size > 2 * 1024 * 1024) { // 2MB limit for logo
      setEditError('El logo no debe superar los 2MB.')
      return
    }
    setEditError('')
    setEditLogoFile(file)
    setEditLogoPreview(URL.createObjectURL(file))
  }

  const handleEditSubmit = async (e) => {
    e.preventDefault()
    setIsUpdating(true)
    setEditError('')
    setEditSuccess('')

    try {
      let imageUrl = editingGroup.image || ''
      if (editImageFile) {
        const imageRef = ref(storage, `research_groups/${Date.now()}_banner_${editImageFile.name}`)
        const snapshot = await uploadBytes(imageRef, editImageFile)
        imageUrl = await getDownloadURL(snapshot.ref)
      }

      let logoUrl = editingGroup.logo || ''
      if (editLogoFile) {
        const logoRef = ref(storage, `research_groups/${Date.now()}_logo_${editLogoFile.name}`)
        const snapshot = await uploadBytes(logoRef, editLogoFile)
        logoUrl = await getDownloadURL(snapshot.ref)
      }

      const projectsArray = editFormData.projects.split('\n').filter(p => p.trim() !== '')
      const pubsArray = editFormData.publications.split('\n').filter(p => p.trim() !== '')
      const reqsArray = editFormData.requirements.split('\n').filter(p => p.trim() !== '')

      const updateData = {
        detailedDescription: editFormData.detailedDescription,
        projects: projectsArray,
        publications: pubsArray,
        contactEmail: editFormData.contactEmail,
        phone: editFormData.phone,
        requirements: reqsArray,
        image: imageUrl,
        logo: logoUrl
      }

      const result = await updateResearchGroup(editingGroup.id, updateData)
      if (result.success) {
        setEditSuccess('Detalles actualizados correctamente.')
        setTimeout(() => {
          loadData()
          setActiveTab('my_groups')
          setEditingGroup(null)
        }, 1500)
      } else {
        setEditError(result.error)
      }
    } catch (err) {
      setEditError('Error al guardar los detalles: ' + err.message)
    }
    setIsUpdating(false)
  }

  // --- DELETE LOGIC ---
  const handleDeleteGroup = async (e, group) => {
    e.stopPropagation()
    if (window.confirm(`¿Estás seguro de que deseas ELIMINAR permanentemente el grupo de investigación "${group.name}"?\nEsta acción es irreversible.`)) {
      try {
        const result = await deleteResearchGroup(group.id, user.uid)
        if (result.success) {
          alert('El grupo de investigación ha sido eliminado.')
          loadData()
          setActiveTab('my_groups')
        } else {
          alert('Error al intentar eliminar: ' + result.error)
        }
      } catch (err) {
        console.error(err)
        alert('Ocurrió un error al intentar eliminar.')
      }
    }
  }

  // --- RENDER HELPERS ---
  const getRandomIcon = (index) => {
    const icons = [<Atom size={24}/>, <Dna size={24}/>, <Microscope size={24}/>]
    return icons[index % icons.length]
  }

  const filteredExplore = allGroups.filter(g => 
    !myGroups.some(mg => mg.id === g.id) && // Hide groups I'm already in
    (g.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
     g.abbr.toLowerCase().includes(searchTerm.toLowerCase()))
  )

  if (loading) return <div className="loading-state">Cargando módulos de investigación...</div>

  return (
    <div className="research-dashboard">
      <div className="research-tabs-container">
        <button 
          className={`research-tab ${activeTab === 'my_groups' ? 'active' : ''}`}
          onClick={() => setActiveTab('my_groups')}
        >
          <Shield size={18} /> Mis Grupos
        </button>
        <button 
          className={`research-tab ${activeTab === 'explore' ? 'active' : ''}`}
          onClick={() => setActiveTab('explore')}
        >
          <Search size={18} /> Explorar / Unirse
        </button>
        <button 
          className={`research-tab ${activeTab === 'create' ? 'active' : ''}`}
          onClick={() => setActiveTab('create')}
        >
          <Plus size={18} /> Crear Nuevo Grupo
        </button>
      </div>

      <div className="research-content-area">
        
        {/* TAB 1: MY GROUPS */}
        {activeTab === 'my_groups' && (
          <div className="my-groups-section">
            <h3>Mis Grupos de Investigación</h3>
            <p>Aquí administras los grupos donde participas como investigador principal o colaborador.</p>
            
            {myGroups.length > 0 ? (
              <div className="research-grid">
                {myGroups.map((group, idx) => (
                  <div key={group.id} className="research-card my-group-card">
                    <div className="card-header">
                      {getRandomIcon(idx)}
                      <span className="abbr-badge">{group.abbr}</span>
                    </div>
                    <h4>{group.name}</h4>
                    <p>{group.description}</p>
                    <div className="card-footer">
                      <span className="members-count">
                        <Users size={14}/> {group.members?.length || 1} Miembros
                      </span>
                      <div className="group-card-actions">
                        {group.createdBy === user.uid && (
                          <div className="owner-actions">
                            <button 
                              className="edit-details-btn" 
                              onClick={(e) => { e.stopPropagation(); handleOpenEdit(group); }}
                              title="Editar Detalles"
                            >
                              <Edit size={16} /> 
                            </button>
                            <button 
                              className="delete-group-btn" 
                              onClick={(e) => handleDeleteGroup(e, group)}
                              title="Eliminar Grupo"
                            >
                              <Trash2 size={16} /> 
                            </button>
                          </div>
                        )}
                        <a href={`/research-group/${group.id}`} target="_blank" rel="noreferrer" className="view-external-btn">
                          <ExternalLink size={16} /> Ver Grupo
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <Atom size={48} />
                <p>Aún no perteneces a ningún grupo oficial.</p>
                <button onClick={() => setActiveTab('explore')}>Explorar Grupos</button>
              </div>
            )}
          </div>
        )}

        {/* TAB 2: EXPLORE */}
        {activeTab === 'explore' && (
          <div className="explore-section">
            <h3>Explorar Grupos Activos</h3>
            <div className="search-bar">
              <Search size={18} />
              <input 
                type="text" 
                placeholder="Buscar por nombre..." 
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="research-grid">
              {filteredExplore.map((group, idx) => (
                <div key={group.id} className="research-card">
                  <div className="card-header">
                    {getRandomIcon(idx)}
                    <span className="abbr-badge">{group.abbr}</span>
                  </div>
                  <h4>{group.name}</h4>
                  <p className="creator-text">Liderado por: {capitalizeName(group.creatorName)}</p>
                  <p>{group.description}</p>
                  <button className="join-btn" onClick={() => handleOpenJoin(group)}>
                    <Lock size={16} /> Unirse al Grupo
                  </button>
                </div>
              ))}
            </div>
            
            {filteredExplore.length === 0 && (
              <p className="no-results">No hay más grupos disponibles para unirte.</p>
            )}
          </div>
        )}

        {/* TAB 3: CREATE */}
        {activeTab === 'create' && (
          <div className="create-group-section">
            <div className="create-form-container">
              <h3>Crear Grupo de Investigación</h3>
              <p>Al crearlo, tú serás el Propietario. Asegúrate de configurar una palabra clave segura para tus futuros miembros.</p>
              
              {createError && <div className="alert error"><AlertCircle size={16}/> {createError}</div>}
              {createSuccess && <div className="alert success"><CheckCircle2 size={16}/> {createSuccess}</div>}
              
              <form onSubmit={handleCreateSubmit}>
                <div className="form-group">
                  <label>Nombre del Grupo *</label>
                  <input type="text" name="name" value={formData.name} onChange={handleCreateChange} placeholder="Ej: Grupo de Inteligencia Artificial" required />
                </div>
                
                <div className="form-group">
                  <label>Siglas *</label>
                  <input type="text" name="abbr" value={formData.abbr} onChange={handleCreateChange} placeholder="Ej: GIA" required maxLength="10"/>
                </div>

                <div className="form-group">
                  <label>Descripción Corta *</label>
                  <textarea name="description" value={formData.description} onChange={handleCreateChange} placeholder="A qué se dedica el grupo..." required />
                </div>

                <div className="form-group">
                  <label>Áreas / Etiquetas (separadas por coma)</label>
                  <input type="text" name="tags" value={formData.tags} onChange={handleCreateChange} placeholder="Ej: Tecnología, Medicina, Robótica" />
                </div>

                <div className="form-group keyword-group">
                  <label>Palabra Clave de Acceso (Contraseña) *</label>
                  <div className="input-with-icon">
                    <Lock size={18} />
                    <input type="text" name="keyword" value={formData.keyword} onChange={handleCreateChange} placeholder="Ej: secreto123" required />
                  </div>
                  <small>Solo los usuarios que tengan esta palabra clave podrán unirse.</small>
                </div>

                <button type="submit" className="submit-btn" disabled={isCreating}>
                  {isCreating ? 'Creando...' : 'Fundar Grupo de Investigación'}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* TAB 4: EDIT GROUP */}
        {activeTab === 'edit_group' && editingGroup && (
          <div className="edit-group-section">
            <button className="back-button-simple" onClick={() => setActiveTab('my_groups')} style={{ marginBottom: '20px', border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 'bold' }}>
               Volver a Mis Grupos
            </button>
            <div className="create-form-container">
              <h3>Editar Detalles de {editingGroup.abbr}</h3>
              <p>Esta información es la que verán los usuarios públicos y los miembros al ingresar a la página detallada del grupo.</p>
              
              {editError && <div className="alert error"><AlertCircle size={16}/> {editError}</div>}
              {editSuccess && <div className="alert success"><CheckCircle2 size={16}/> {editSuccess}</div>}
              
              <form onSubmit={handleEditSubmit}>
                <div className="form-group" style={{ marginBottom: '25px' }}>
                  <label>Portada del Grupo (Recomendado: 1200x400px)</label>
                  <input 
                    type="file" 
                    accept="image/*" 
                    onChange={(e) => {
                      if(e.target.files && e.target.files[0]) handleImageSelection(e.target.files[0])
                    }} 
                    style={{ display: 'none' }} 
                    ref={fileInputRef} 
                  />
                  <div 
                    className="image-upload-area" 
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleImageDrop}
                    style={{ minHeight: '120px', border: '2px dashed #cbd5e1', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', backgroundColor: '#f8fafc', overflow: 'hidden' }}
                  >
                    {editImagePreview ? (
                      <div className="preview-container" style={{ position: 'relative', width: '100%', display: 'flex', justifyContent: 'center' }}>
                         <img src={editImagePreview} alt="Banner Preview" style={{ maxHeight: '150px', width: '100%', objectFit: 'cover' }} />
                         <button type="button" onClick={(e) => { e.stopPropagation(); setEditImagePreview(''); setEditImageFile(null); }} style={{ position: 'absolute', top: '10px', right: '10px', background: 'rgba(255,0,0,0.8)', color: 'white', border: 'none', borderRadius: '50%', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 10 }}>
                            <X size={16} />
                         </button>
                      </div>
                    ) : (
                      <>
                        <Upload size={24} color="#94a3b8" style={{ marginBottom: '8px' }} />
                        <span style={{ fontSize: '14px' }}>Subir Portada Principal</span>
                        <small style={{ fontSize: '12px' }}>Dimensiones ideales: 1200x400 px</small>
                      </>
                    )}
                  </div>
                </div>

                <div className="form-group" style={{ marginBottom: '25px' }}>
                  <label>Logo del Grupo (Recomendado: Cuadrado, 500x500px)</label>
                  <input 
                    type="file" 
                    accept="image/*" 
                    onChange={(e) => {
                      if(e.target.files && e.target.files[0]) handleLogoSelection(e.target.files[0])
                    }} 
                    style={{ display: 'none' }} 
                    ref={logoInputRef} 
                  />
                  <div 
                    className="image-upload-area" 
                    onClick={() => logoInputRef.current?.click()}
                    style={{ 
                      width: '150px', 
                      height: '150px', 
                      minWidth: '150px',
                      minHeight: '150px',
                      aspectRatio: '1/1',
                      border: '2px dashed #cbd5e1', 
                      borderRadius: '50%', /* Circular preview */
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      cursor: 'pointer', 
                      backgroundColor: '#f8fafc', 
                      overflow: 'hidden', 
                      margin: '0 auto' 
                    }}
                  >
                    {editLogoPreview ? (
                      <div className="preview-container" style={{ position: 'relative', width: '100%', height: '100%' }}>
                         <img src={editLogoPreview} alt="Logo Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                         <button type="button" onClick={(e) => { e.stopPropagation(); setEditLogoPreview(''); setEditLogoFile(null); }} style={{ position: 'absolute', top: '5px', right: '5px', background: 'rgba(255,0,0,0.8)', color: 'white', border: 'none', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 10 }}>
                            <X size={12} />
                         </button>
                      </div>
                    ) : (
                      <>
                        <ImageIcon size={24} color="#94a3b8" style={{ marginBottom: '8px' }} />
                        <span style={{ fontSize: '12px', textAlign: 'center', padding: '0 5px' }}>Subir Logo</span>
                      </>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label>Descripción Larga (Misión, visión, contexto del grupo)</label>
                  <textarea name="detailedDescription" value={editFormData.detailedDescription} onChange={handleEditChange} placeholder="Nuestra misión es investigar profundamente en..." rows="5" />
                </div>

                <div className="form-group">
                  <label>Proyectos en Curso (Uno por línea)</label>
                  <textarea name="projects" value={editFormData.projects} onChange={handleEditChange} placeholder="- Proyecto A&#10;- Análisis B" rows="4" />
                </div>

                <div className="form-group">
                  <label>Publicaciones Recientes (Una por línea, opcional)</label>
                  <textarea name="publications" value={editFormData.publications} onChange={handleEditChange} placeholder="- Título de mi publicación (2025)" rows="3" />
                </div>

                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div className="form-group">
                    <label>Correo de Contacto</label>
                    <div className="input-with-icon">
                      <Mail size={18} />
                      <input type="email" name="contactEmail" value={editFormData.contactEmail} onChange={handleEditChange} placeholder="admin@grupo.hn" />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Número Telefónico</label>
                    <div className="input-with-icon">
                      <Phone size={18} />
                      <input type="tel" name="phone" value={editFormData.phone} onChange={handleEditChange} placeholder="+504 9999-9999" />
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label>Requisitos para Unirse (Uno por línea)</label>
                  <textarea name="requirements" value={editFormData.requirements} onChange={handleEditChange} placeholder="- Ser estudiante de Ingeniería&#10;- Promedio mayor a 80%" rows="3" />
                </div>

                <button type="submit" className="submit-btn" disabled={isUpdating}>
                  {isUpdating ? 'Guardando cambios...' : 'Guardar Detalles Públicos'}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* JOIN MODAL */}
      {showJoinModal && selectedGroup && (
        <div className="modal-overlay">
          <div className="modal-content join-modal">
            <h3>Unirse a {selectedGroup.abbr}</h3>
            <p>Ingresa la palabra clave proporcionada por el propietario del grupo para obtener acceso oficial como Investigador.</p>
            
            {joinError && <div className="alert error"><AlertCircle size={16}/> {joinError}</div>}
            {joinSuccess && <div className="alert success"><CheckCircle2 size={16}/> {joinSuccess}</div>}

            <form onSubmit={handleJoinSubmit}>
              <div className="form-group">
                <div className="input-with-icon">
                  <Unlock size={18} />
                  <input 
                    type="password" 
                    placeholder="Escribe la palabra secreta..." 
                    value={joinKeyword} 
                    onChange={(e) => setJoinKeyword(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="cancel-btn" onClick={() => setShowJoinModal(false)}>Cancelar</button>
                <button type="submit" className="submit-btn" disabled={isJoining}>Confirmar Acceso</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  )
}

export default ResearchGroupsDashboard
